import { Box, DialogContent, Typography } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BglDialog, BglDialogTitle } from "components/dialog/BglDialogStyles";
import { BglLoading } from "components/loading/BglLoading";
import { PreviewArea } from "components/wysiwyg/PreviewArea";
import { useGlobalConfig } from "context/GlobalContext";
import { WidgetSdkProvider } from "context/WidgetSdkContext";
import { WidgetSdkErrorBoundary } from "context/WidgetSdkErrorBoundary";
import { createCxApiClient } from "framework/http/CxEditorClient";
import { getDefaultFuelType } from "hooks/useProjectFuelTypes";
import { selectProjectAttributes } from "store/dataReducers/applicationScopeSlice";
import {
  setDataScenarios,
  setDataScenariosError,
  setDataScenariosLoading,
} from "store/dataReducers/dataScenariosSlice";
import { selectSelectedPilotId } from "store/dataReducers/pilotsSlice";
import { DataScenario, RenderRequest } from "types/apis/cxEditor";
import { getAvailableFuelTypes } from "features/contentManagement/dataScenarios/dataScenariosUtils";
import type { ProjectAttributesForFuel } from "utils/template-list";

const SURVEY_TEMPLATE_KEY = "DASHBOARD.SURVEY";

function filterSurveyWebScenarios(dataScenarios: DataScenario[]): DataScenario[] {
  const surveyScenarios = dataScenarios.filter((ds) => {
    const isWeb = (ds.productType || "").toLowerCase() === "web";
    return isWeb && ds.templateName === SURVEY_TEMPLATE_KEY;
  });

  if (surveyScenarios.length > 0) {
    return surveyScenarios;
  }

  return dataScenarios.filter((ds) => {
    const isWeb = (ds.productType || "").toLowerCase() === "web";
    return isWeb && ds.templateName === "DASHBOARD";
  });
}

function resolveDefaultSurveyScenarioId(dataScenarios: DataScenario[]): string | null {
  const filtered = filterSurveyWebScenarios(dataScenarios);
  if (filtered.length === 0) {
    return null;
  }

  const defaultByFlag = filtered.find((ds) => ds.defaultScenario === true);
  if (defaultByFlag?.scenarioId) {
    return defaultByFlag.scenarioId;
  }

  return filtered[0]?.scenarioId || null;
}

interface SurveyTemplatePreviewDialogProps {
  open: boolean;
  onClose: () => void;
  locale: string;
}

export const SurveyTemplatePreviewDialog: React.FC<SurveyTemplatePreviewDialogProps> = ({
  open,
  onClose,
  locale,
}) => {
  const { rc } = useGlobalConfig();
  const dispatch = useDispatch();
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const projectAttributes = useSelector(selectProjectAttributes);

  const [resolvedScenarioId, setResolvedScenarioId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const availableFuelTypes = useMemo(() => {
    if (projectAttributes?.fuel_type) {
      const fuelTypeValue =
        typeof projectAttributes.fuel_type === "string"
          ? projectAttributes.fuel_type
          : String(projectAttributes.fuel_type);
      return getAvailableFuelTypes(fuelTypeValue);
    }
    return ["ELECTRIC"];
  }, [projectAttributes]);

  const fuelType = useMemo(
    () =>
      getDefaultFuelType(
        SURVEY_TEMPLATE_KEY,
        projectAttributes as ProjectAttributesForFuel | null,
        availableFuelTypes
      ),
    [projectAttributes, availableFuelTypes]
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) {
      setResolvedScenarioId(null);
      setError(null);
      setLoading(false);
      return;
    }

    if (!selectedPilotId) {
      setError("No pilot selected.");
      setLoading(false);
      return;
    }

    let ignore = false;
    const pilotId = String(selectedPilotId);

    async function fetchAndResolveScenarios() {
      setLoading(true);
      setError(null);
      setResolvedScenarioId(null);

      dispatch(setDataScenariosLoading({ pilotId, fuelType, locale, loading: true }));

      try {
        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        const dataScenariosResp = await api.getDataScenarios(pilotId, fuelType, locale);

        if (ignore) {
          return;
        }

        const dataScenarios = dataScenariosResp?.dataScenarios as DataScenario[] | undefined;

        if (dataScenarios?.length) {
          dispatch(setDataScenarios({ pilotId, fuelType, locale, dataScenarios }));
        }

        const scenarioId = dataScenarios?.length
          ? resolveDefaultSurveyScenarioId(dataScenarios)
          : null;

        if (!scenarioId) {
          setError("No Web data scenario found for Survey.");
          return;
        }

        setResolvedScenarioId(scenarioId);
      } catch (fetchError: any) {
        if (!ignore) {
          const message = fetchError?.message || "Failed to load data scenarios.";
          dispatch(setDataScenariosError({ pilotId, fuelType, locale, error: message }));
          setError(message);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    fetchAndResolveScenarios();

    return () => {
      ignore = true;
    };
  }, [open, selectedPilotId, fuelType, locale, rc, dispatch]);

  const renderRequest: RenderRequest | null = useMemo(() => {
    if (!resolvedScenarioId) {
      return null;
    }

    return {
      templateKey: SURVEY_TEMPLATE_KEY,
      fuelType,
      elementKey: "Full Template",
      variant: {
        hierarchy: "Project",
        locale,
        scenario: resolvedScenarioId,
      },
      previewMode: "desktop",
    };
  }, [resolvedScenarioId, fuelType, locale]);

  return (
    <BglDialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "min(1200px, 95vw)",
          maxWidth: "none",
        },
      }}
    >
      <BglDialogTitle title="Survey Preview" onClose={handleClose} />
      <DialogContent
        sx={{
          p: 2,
          minHeight: 500,
          height: "70vh",
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading && (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <BglLoading message="Loading preview…" />
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
            <Typography variant="body2" color="error" textAlign="center">
              {error}
            </Typography>
          </Box>
        )}

        {!loading && !error && resolvedScenarioId && renderRequest && (
          <WidgetSdkErrorBoundary>
            <WidgetSdkProvider>
              <Box sx={{ flex: 1, minHeight: 400 }}>
                <PreviewArea
                  request={renderRequest}
                  channel="Web"
                  previewMode="desktop"
                  isFullTemplateSelected
                  elementKey="Full Template"
                  variant={{ scenario: resolvedScenarioId }}
                />
              </Box>
            </WidgetSdkProvider>
          </WidgetSdkErrorBoundary>
        )}
      </DialogContent>
    </BglDialog>
  );
};
